import { SlidersHorizontal } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { Tag } from '@/hooks/useTags';
import Loading from '../shared/Loading';

type BulkDraftStatus = 'unchanged' | 'draft' | 'final';

export type BulkEditableFile = {
  id?: string;
  guid?: string;
  is_draft?: boolean;
  is_drafting_managed?: boolean;
  tags?: string[];
  displayName?: string;
  fileName?: string;
};

export type BulkEditChanges = {
  draftTarget?: boolean;
  draftDocumentGuids: string[];
  tagDocumentGuids: string[];
  tagGuids: string[];
};

type BulkPreviewRow = {
  id: string;
  name: string;
  currentDraftStatus: boolean;
  draftWillChange: boolean;
  tagsToAdd: Tag[];
  hasAnyChange: boolean;
};

type BulkEditFilesDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedFiles: BulkEditableFile[];
  enableTags: boolean;
  tags: Tag[];
  isLoadingTags: boolean;
  isApplying: boolean;
  onApply: (changes: BulkEditChanges) => Promise<void>;
};

export function BulkEditFilesDialog({
  open,
  onOpenChange,
  selectedFiles,
  enableTags,
  tags,
  isLoadingTags,
  isApplying,
  onApply,
}: BulkEditFilesDialogProps) {
  const [draftStatus, setDraftStatus] = useState<BulkDraftStatus>('unchanged');
  const [selectedTagGuids, setSelectedTagGuids] = useState<string[]>([]);

  useEffect(() => {
    if (!open) {
      setDraftStatus('unchanged');
      setSelectedTagGuids([]);
    }
  }, [open]);

  const selectedTags = useMemo(
    () => enableTags ? tags.filter((tag) => selectedTagGuids.includes(tag.guid)) : [],
    [enableTags, selectedTagGuids, tags]
  );
  const draftTarget =
    draftStatus === 'draft' ? true :
    draftStatus === 'final' ? false :
    undefined;
  const finalStatusDisabled = selectedFiles.some(
    (document) => document.is_draft && document.is_drafting_managed
  );

  const previewRows = useMemo<BulkPreviewRow[]>(() => (
    selectedFiles.map((document, index) => {
      const currentTags = document.tags ?? [];
      const tagsToAdd = selectedTags.filter((tag) => !currentTags.includes(tag.name));
      const currentDraftStatus = Boolean(document.is_draft);
      const draftWillChange = draftTarget !== undefined && currentDraftStatus !== draftTarget;

      return {
        id: document.id || document.guid || `selected-file-${index}`,
        name: document.displayName || document.fileName || document.guid || document.id || 'Untitled file',
        currentDraftStatus,
        draftWillChange,
        tagsToAdd,
        hasAnyChange: draftWillChange || tagsToAdd.length > 0,
      };
    })
  ), [draftTarget, selectedFiles, selectedTags]);

  const draftChangeCount = previewRows.filter((row) => row.draftWillChange).length;
  const tagChangeFileCount = previewRows.filter((row) => row.tagsToAdd.length > 0).length;
  const changedPreviewRows = previewRows.filter((row) => row.hasAnyChange);
  const filesWithChangesCount = changedPreviewRows.length;
  const unchangedFileCount = selectedFiles.length - filesWithChangesCount;
  const hasChanges = draftChangeCount > 0 || tagChangeFileCount > 0;
  const hasInput = draftTarget !== undefined || selectedTags.length > 0;

  const toggleTag = (tagGuid: string, checked: boolean | 'indeterminate') => {
    setSelectedTagGuids((current) => {
      if (checked === true) {
        return current.includes(tagGuid) ? current : [...current, tagGuid];
      }
      return current.filter((guid) => guid !== tagGuid);
    });
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      setDraftStatus('unchanged');
      setSelectedTagGuids([]);
    }
    onOpenChange(nextOpen);
  };

  const handleApply = async () => {
    if (!hasChanges) return;

    const draftDocumentGuids = selectedFiles
      .filter((file) => draftTarget !== undefined && Boolean(file.is_draft) !== draftTarget)
      .map((file) => file.guid)
      .filter((guid): guid is string => Boolean(guid));
    const tagDocumentGuids = selectedTags.length > 0
      ? selectedFiles
        .filter((file) => selectedTags.some((tag) => !(file.tags ?? []).includes(tag.name)))
        .map((file) => file.guid)
        .filter((guid): guid is string => Boolean(guid))
      : [];

    await onApply({
      draftTarget,
      draftDocumentGuids,
      tagDocumentGuids,
      tagGuids: enableTags ? selectedTagGuids : [],
    });
  };

  const draftStatusControl = (
    <div className="space-y-2">
      <div className="flex h-5 items-center">
        <label className="text-sm font-medium text-stone-900">Draft status</label>
      </div>
      <Select
        value={draftStatus}
        onValueChange={(value) => setDraftStatus(value as BulkDraftStatus)}
      >
        <SelectTrigger
          tabIndex={-1}
          className="outline-none focus:!outline-none focus:!ring-0 focus:!ring-offset-0 focus-visible:!outline-none focus-visible:!ring-0 focus-visible:!ring-offset-0 data-[state=open]:!ring-0 data-[state=open]:!ring-offset-0"
        >
          <SelectValue placeholder="Draft status" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="unchanged">Leave unchanged</SelectItem>
          <SelectItem value="draft">Mark as draft</SelectItem>
          <SelectItem value="final" disabled={finalStatusDisabled}>Mark as final</SelectItem>
        </SelectContent>
      </Select>
      {finalStatusDisabled && (
        <p className="text-xs text-stone-500">
          The selection includes a draft created in Drafting, which cannot currently be marked final.
        </p>
      )}
    </div>
  );

  const tagsControl = enableTags ? (
    <div className="space-y-2">
      <div className="flex h-5 items-center justify-between gap-2">
        <span className="text-sm font-medium text-stone-900">Tags to add</span>
        {selectedTagGuids.length > 0 && (
          <button
            type="button"
            className="text-xs font-medium text-stone-500 hover:text-stone-900"
            onClick={() => setSelectedTagGuids([])}
          >
            Clear
          </button>
        )}
      </div>

      {isLoadingTags ? (
        <div className="rounded-md border border-stone-200 p-3">
          <Loading text="Loading tags..." inline />
        </div>
      ) : tags.length === 0 ? (
        <div className="rounded-md border border-stone-200 bg-stone-50 p-3 text-sm text-stone-600">
          No tags have been created for this matter yet.
        </div>
      ) : (
        <div className="h-80 overflow-y-auto rounded-md border border-stone-200">
          {tags.map((tag) => {
            const checked = selectedTagGuids.includes(tag.guid);
            return (
              <label
                key={tag.guid}
                className="flex cursor-pointer items-center gap-3 border-b border-stone-100 px-3 py-2 last:border-b-0 hover:bg-stone-50"
              >
                <Checkbox
                  checked={checked}
                  onCheckedChange={(nextChecked) => toggleTag(tag.guid, nextChecked)}
                />
                <span
                  className="h-3 w-3 rounded-full border border-stone-200"
                  style={{ backgroundColor: tag.color || '#CCCCCC' }}
                />
                <span className="min-w-0 flex-1 truncate text-sm font-medium text-stone-900">
                  {tag.name}
                </span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  ) : null;

  const summary = (
    <div className="flex h-10 shrink-0 items-center rounded-lg border border-stone-200 bg-stone-50 px-3 text-xs text-stone-500">
      <span className="font-medium text-stone-900">{filesWithChangesCount}</span>
      <span className="ml-1">file{filesWithChangesCount !== 1 ? 's' : ''} changing</span>
      <span className="mx-2 text-stone-300">/</span>
      <span>{draftChangeCount} status change{draftChangeCount !== 1 ? 's' : ''}</span>
      {enableTags && (
        <>
          <span className="mx-2 text-stone-300">/</span>
          <span className="truncate">{tagChangeFileCount} file{tagChangeFileCount !== 1 ? 's' : ''} receiving tags</span>
        </>
      )}
    </div>
  );

  const preview = (
    <>
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-md border border-stone-200">
        <div className="h-9 shrink-0 border-b border-stone-200 bg-white px-3 py-2 text-xs font-medium uppercase tracking-wide text-stone-500">
          Preview
        </div>

        {changedPreviewRows.length === 0 ? (
          <div className="flex min-h-0 flex-1 items-center justify-center px-6 py-12 text-center">
            <div>
              <p className="text-sm font-medium text-stone-900">
                {hasInput ? 'Selected edits do not change these files' : 'No file changes yet'}
              </p>
              <p className="mt-1 text-sm text-stone-500">
                {hasInput
                  ? `Every selected file already has the requested ${enableTags ? 'status and tags' : 'status'}.`
                  : `Choose a draft status${enableTags ? ' or select tags' : ''} to preview exactly which files will be updated.`}
              </p>
            </div>
          </div>
        ) : (
          <div className="min-h-0 flex-1 overflow-y-auto">
            {changedPreviewRows.map((previewRow) => {
              const currentDraftLabel = previewRow.currentDraftStatus ? 'Draft' : 'Final';
              const nextDraftLabel = draftTarget === undefined ? null : draftTarget ? 'Draft' : 'Final';

              return (
                <div
                  key={previewRow.id}
                  className="grid min-h-11 grid-cols-[minmax(0,1fr)_160px] items-center gap-3 border-b border-stone-100 px-3 py-1.5 last:border-b-0"
                >
                  <div className="min-w-0 truncate text-xs font-medium leading-5 text-stone-900">
                    {previewRow.name}
                  </div>

                  <div className="min-w-0 space-y-0.5">
                    {previewRow.draftWillChange && nextDraftLabel && (
                      <div className="grid min-w-0 grid-cols-[38px_minmax(0,1fr)] gap-1.5 text-[11px] leading-4">
                        <span className="font-medium uppercase tracking-wide text-stone-400">
                          Draft
                        </span>
                        <span className="min-w-0 truncate">
                          <span className="text-stone-500">{currentDraftLabel}</span>
                          <span className="px-1.5 text-stone-400">-&gt;</span>
                          <span className="font-medium text-stone-900">{nextDraftLabel}</span>
                        </span>
                      </div>
                    )}

                    {previewRow.tagsToAdd.length > 0 && (
                      <div className="grid min-w-0 grid-cols-[38px_minmax(0,1fr)] gap-1.5 text-[11px] leading-4">
                        <span className="font-medium uppercase tracking-wide text-stone-400">
                          Tags
                        </span>
                        <TooltipProvider delayDuration={200}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button
                                type="button"
                                className="w-fit rounded-sm text-left font-medium text-stone-600 underline decoration-stone-300 underline-offset-2 hover:text-stone-900 focus:outline-hidden focus:ring-1 focus:ring-primary"
                              >
                                +{previewRow.tagsToAdd.length} tag{previewRow.tagsToAdd.length !== 1 ? 's' : ''}
                              </button>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs">
                              <div className="space-y-1">
                                {previewRow.tagsToAdd.map((tag) => (
                                  <div
                                    key={tag.guid}
                                    className="flex items-center gap-2 text-xs"
                                  >
                                    <span
                                      className="h-2 w-2 shrink-0 rounded-full"
                                      style={{ backgroundColor: tag.color || '#CCCCCC' }}
                                    />
                                    <span className="truncate">{tag.name}</span>
                                  </div>
                                ))}
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {unchangedFileCount > 0 && hasChanges && (
        <p className="text-xs text-stone-500">
          {unchangedFileCount} selected file{unchangedFileCount !== 1 ? 's' : ''} already match the selected edits and will not be updated.
        </p>
      )}
    </>
  );

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className={
          enableTags
            ? "flex h-[82vh] max-h-[760px] flex-col overflow-hidden sm:max-w-5xl"
            : "flex h-[72vh] max-h-[640px] flex-col overflow-hidden sm:max-w-3xl"
        }
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        <DialogHeader className="shrink-0">
          <DialogTitle>Edit Files</DialogTitle>
          <DialogDescription>
            Review changes for {selectedFiles.length} selected file{selectedFiles.length !== 1 ? 's' : ''}.
          </DialogDescription>
        </DialogHeader>

        {enableTags ? (
          <div className="grid min-h-0 flex-1 gap-4 lg:grid-cols-[280px_minmax(0,1fr)]">
            <div className="min-h-0 space-y-3 overflow-hidden">
              {draftStatusControl}
              {tagsControl}
            </div>

            <div className="flex min-w-0 min-h-0 flex-col gap-3 lg:pt-7">
              {summary}
              {preview}
            </div>
          </div>
        ) : (
          <div className="flex min-h-0 flex-1 flex-col gap-3">
            <div className="grid shrink-0 gap-3 sm:grid-cols-[minmax(220px,320px)_minmax(0,1fr)] sm:items-end">
              {draftStatusControl}
              {summary}
            </div>

            {preview}
          </div>
        )}

        <DialogFooter className="shrink-0">
          <Button
            type="button"
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={isApplying}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleApply}
            disabled={isApplying || !hasChanges || selectedFiles.length === 0}
          >
            <SlidersHorizontal className="h-4 w-4" />
            {isApplying ? 'Applying...' : `Apply to ${filesWithChangesCount} file${filesWithChangesCount !== 1 ? 's' : ''}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
